import { startAnnualCheckout } from './billingActions.js';
import { getCheckoutReturnPath } from './learnMoreNavigation.js';

export const JOIN_CHECKOUT_INTENT = 'join';

/**
 * @param {string | null | undefined} intent
 */
export function isJoinCheckoutIntent(intent) {
  return intent === JOIN_CHECKOUT_INTENT;
}

/**
 * @param {string} returnTo
 */
export function buildJoinAuthState(returnTo) {
  return {
    from: returnTo,
    intent: JOIN_CHECKOUT_INTENT,
  };
}

/**
 * @param {string} from
 * @param {string | null | undefined} intent
 */
export function buildAuthNavigationState(from, intent) {
  if (isJoinCheckoutIntent(intent)) {
    return buildJoinAuthState(from);
  }
  return { from };
}

/**
 * @param {{ pathname?: string, search?: string }} location
 */
export function buildJoinAuthStateFromLocation(location) {
  return buildJoinAuthState(getCheckoutReturnPath(location));
}

/**
 * @param {Error & { code?: string }} error
 */
export function formatJoinCheckoutError(error) {
  if (error.code === 'EMAIL_VERIFICATION_REQUIRED') {
    return 'Please verify your email before purchasing annual access. You can resend the verification email from your profile.';
  }
  if (error.code === 'STUB_DISABLED' || error.code === 'STRIPE_NOT_CONFIGURED') {
    return 'Online checkout is not available yet. Please try again later.';
  }
  return error.message || 'Checkout failed. Please try again.';
}

/**
 * @param {object} options
 * @param {string} options.returnTo
 * @param {() => Promise<void>} [options.refreshAccess]
 * @param {import('react-router-dom').NavigateFunction} [options.navigate]
 */
export async function runJoinCheckoutAfterAuth({ returnTo, refreshAccess, navigate }) {
  return runMembershipCheckoutAfterAuth({
    returnTo,
    refreshAccess,
    navigate,
    intent: 'join',
  });
}

/**
 * @param {object} options
 * @param {string} options.returnTo
 * @param {'join' | 'renew'} [options.intent]
 * @param {() => Promise<void>} [options.refreshAccess]
 * @param {import('react-router-dom').NavigateFunction} [options.navigate]
 */
export async function runMembershipCheckoutAfterAuth({
  returnTo,
  refreshAccess,
  navigate,
  intent = 'join',
}) {
  const result = await startAnnualCheckout({ intent, returnTo });

  if (result.redirecting) {
    return result;
  }

  if (refreshAccess) {
    await refreshAccess();
  }

  if (navigate && returnTo) {
    navigate(returnTo);
  }

  return result;
}

/**
 * Start join checkout for a signed-in trial user from the current page.
 *
 * @param {object} options
 * @param {{ pathname?: string, search?: string }} options.location
 * @param {() => Promise<void>} [options.refreshAccess]
 */
export async function startJoinCheckoutFromLocation({ location, refreshAccess }) {
  return startMembershipCheckoutFromLocation({ location, refreshAccess, intent: 'join' });
}

/**
 * Start membership checkout from the current page.
 *
 * @param {object} options
 * @param {{ pathname?: string, search?: string }} options.location
 * @param {'join' | 'renew'} [options.intent]
 * @param {() => Promise<void>} [options.refreshAccess]
 */
export async function startMembershipCheckoutFromLocation({
  location,
  refreshAccess,
  intent = 'join',
}) {
  const returnTo = getCheckoutReturnPath(location);
  return runMembershipCheckoutAfterAuth({ returnTo, refreshAccess, intent });
}
